"use client";

import { c as _c } from "react/compiler-runtime";
import { readFragment } from "@opensea/graphql";
import { useNumberFormatter } from "@opensea/i18n";
import { Button } from "@opensea/next-ui-kit";
import { Flex, FlexColumn, QuantityInput, Separator, SpaceBetween, TextBody, TextBodySkeleton, TextHeading } from "@opensea/ui-kit";
import { UnreachableCaseError } from "@opensea/utils";
import { useAccount } from "@opensea/wallet";
import { useTranslations } from "next-intl";
import { useShallow } from "zustand/react/shallow";
import { DropStatus, MAX_MINTABLE_MAX } from "@/components/collection/drops/constants";
import { useFormatStageDate } from "@/components/collection/drops/use-format-stage-date";
import { TokenPrice } from "@/components/price/TokenPrice";
import { UsdPrice } from "@/components/price/UsdPrice";
import { useAuthenticatedCallback } from "@/hooks/use-authenticated-callback";
import { useDropStore } from "../CollectionDropContext";
import { MintButton } from "../MintButton";
import { MintCTAModuleFragment } from "./MintCTAModule.fragment";
function MintWalletLimit(t0) {
  const $ = _c(6);
  const {
    perWalletLimit
  } = t0;
  const format = useNumberFormatter();
  const t = useTranslations("MintCTAModule");
  let t1;
  if ($[0] !== format || $[1] !== perWalletLimit || $[2] !== t) {
    t1 = t("limit", {
      limit: format(perWalletLimit, {
        display: "full",
        custom: {
          maximumFractionDigits: 0
        }
      })
    });
    $[0] = format;
    $[1] = perWalletLimit;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <TextBody className="font-mono text-text-secondary uppercase" size="xs">{t1}</TextBody>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function MintCta(t0) {
  const $ = _c(26);
  const {
    perWalletLimit,
    isPreview
  } = t0;
  const [collectionSlug, status, setIsReminderModalOpen, maxMints, isLoadingEligibility] = useDropStore(useShallow(_temp));
  const {
    ready,
    authenticated
  } = useAccount();
  const hasMintsRemaining = !maxMints || maxMints > 0;
  const t = useTranslations("MintCTAModule");
  let t1;
  if ($[0] !== setIsReminderModalOpen) {
    t1 = () => {
      setIsReminderModalOpen(true);
    };
    $[0] = setIsReminderModalOpen;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const openReminderModal = useAuthenticatedCallback(t1);
  if (!status) {
    return null;
  }
  switch (status) {
    case DropStatus.MINTING_SOON:
      {
        let t2;
        if ($[2] !== t) {
          t2 = t("setReminder");
          $[2] = t;
          $[3] = t2;
        } else {
          t2 = $[3];
        }
        let t3;
        if ($[4] !== openReminderModal || $[5] !== t2) {
          t3 = <Button onClick={openReminderModal} variant="secondary">{t2}</Button>;
          $[4] = openReminderModal;
          $[5] = t2;
          $[6] = t3;
        } else {
          t3 = $[6];
        }
        return t3;
      }
    case DropStatus.MINTING:
      {
        let t2;
        if ($[7] !== collectionSlug || $[8] !== isPreview || $[9] !== t) {
          t2 = isPreview ? <Flex className="gap-4"><QuantityInput disabled={true} onValueChange={_temp2} size="lg" value={1} /><Button disabled={true} size="lg">{t("mint")}</Button></Flex> : <MintButton collectionSlug={collectionSlug} size="lg" />;
          $[7] = collectionSlug;
          $[8] = isPreview;
          $[9] = t;
          $[10] = t2;
        } else {
          t2 = $[10];
        }
        let t3;
        if ($[11] !== authenticated || $[12] !== hasMintsRemaining || $[13] !== isLoadingEligibility || $[14] !== perWalletLimit || $[15] !== ready || $[16] !== t) {
          t3 = isLoadingEligibility || !ready ? <TextBodySkeleton size="xs" /> : (hasMintsRemaining || !authenticated) && perWalletLimit ? <MintWalletLimit perWalletLimit={perWalletLimit} /> : hasMintsRemaining ? null : <TextBody className="font-mono text-text-tertiary uppercase" size="xs">{t("mintLimitReached")}</TextBody>;
          $[11] = authenticated;
          $[12] = hasMintsRemaining;
          $[13] = isLoadingEligibility;
          $[14] = perWalletLimit;
          $[15] = ready;
          $[16] = t;
          $[17] = t3;
        } else {
          t3 = $[17];
        }
        let t4;
        if ($[18] !== t2 || $[19] !== t3) {
          t4 = <FlexColumn className="w-full gap-5 sm:w-auto sm:items-end">{t2}{t3}</FlexColumn>;
          $[18] = t2;
          $[19] = t3;
          $[20] = t4;
        } else {
          t4 = $[20];
        }
        return t4;
      }
    case DropStatus.MINT_ENDED:
      {}
    case DropStatus.MINTED_OUT:
      {
        const t2 = `/collection/${String(collectionSlug)}`;
        let t3;
        if ($[21] !== t) {
          t3 = t("viewItems");
          $[21] = t;
          $[22] = t3;
        } else {
          t3 = $[22];
        }
        let t4;
        if ($[23] !== t2 || $[24] !== t3) {
          t4 = <Button href={t2}>{t3}</Button>;
          $[23] = t2;
          $[24] = t3;
          $[25] = t4;
        } else {
          t4 = $[25];
        }
        return t4;
      }
    default:
      {
        throw new UnreachableCaseError(status);
      }
  }
}
function _temp2() {}
function _temp(state) {
  return [state.collectionSlug, state.status, state.setIsReminderModalOpen, state.maxMints, state.isLoadingEligibility];
}
export function MintCTAModule(t0) {
  const $ = _c(21);
  let dropKey;
  let isPreview;
  let rest;
  if ($[0] !== t0) {
    ({
      drop: dropKey,
      isPreview,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = dropKey;
    $[2] = isPreview;
    $[3] = rest;
  } else {
    dropKey = $[1];
    isPreview = $[2];
    rest = $[3];
  }
  const [status, totalSupply, activeStageIndex, maxSupply, eligiblePrice, stages] = useDropStore(useShallow(_temp3));
  const drop = readFragment(MintCTAModuleFragment, dropKey);
  let t1;
  if ($[4] !== activeStageIndex) {
    t1 = stage => stage.stageIndex === activeStageIndex;
    $[4] = activeStageIndex;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const activeStageFromStore = stages.find(t1);
  const activeStageFromFragment = drop?.stages.find(stage_0 => stage_0.stageIndex === activeStageIndex);
  const activeStage = activeStageFromStore || activeStageFromFragment;
  const t = useTranslations("MintCTAModule");
  const format = useNumberFormatter();
  const formatStageDate = useFormatStageDate();
  const perWalletLimit = activeStage?.eligibleMaxTotalMintableByWallet ?? activeStage?.maxTotalMintableByWallet ?? 0;
  const showPerWalletLimit = perWalletLimit !== maxSupply && perWalletLimit !== MAX_MINTABLE_MAX;
  const title = status === DropStatus.MINT_ENDED ? t("mintEnded") : activeStage?.label || t("publicStage");
  const renderSubtitle = function renderSubtitle() {
    switch (status) {
      case DropStatus.MINTING_SOON:
        {
          return activeStage ? <><TextBody className="text-text-secondary" size="sm">{formatStageDate(activeStage.startTime)}</TextBody>{showPerWalletLimit ? <MintWalletLimit perWalletLimit={perWalletLimit} /> : null}</> : null;
        }
      case DropStatus.MINTING:
        {
          return activeStage ? <Flex className="items-center gap-2"><TextBody className="text-text-secondary" size="sm"><UsdPrice price={eligiblePrice ?? activeStage.price} /></TextBody><Separator className="h-3" orientation="vertical" /><Flex className="items-center gap-2"><div className="size-2 animate-pulse rounded-full bg-success-1" /><TextBody className="font-mono text-success-1 uppercase" size="sm">{t("mintingNow")}</TextBody></Flex></Flex> : null;
        }
      case DropStatus.MINTED_OUT:
        {
          return <TextBody className="text-text-secondary" size="sm">{t("mintedOut")}</TextBody>;
        }
      case DropStatus.MINT_ENDED:
        {
          return <TextBody className="text-text-secondary" size="sm">{t("mintEndedAllItemsMinted", {
              count: format(totalSupply, {
                display: "full",
                custom: {
                  maximumFractionDigits: 0
                }
              })
            })}</TextBody>;
        }
      default:
        {
          return null;
        }
    }
  };
  if (!(activeStage && drop)) {
    return null;
  }
  const T0 = SpaceBetween;
  const t2 = "w-full flex-col items-center gap-6 rounded-md border border-border-1 bg-bg-additional-1 p-6 sm:flex-row sm:gap-0";
  const T1 = FlexColumn;
  const t3 = "w-full";
  let t4;
  if ($[6] !== title) {
    t4 = <TextHeading className="mb-2" size="sm">{title}</TextHeading>;
    $[6] = title;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = status !== "MINT_ENDED" ? <TextBody className="mb-1 font-mono uppercase" weight="semibold">{status === "MINTED_OUT" ? t("soldOut") : <TokenPrice price={eligiblePrice ?? activeStage.price} />}</TextBody> : null;
  const t6 = renderSubtitle();
  let t7;
  if ($[8] !== T1 || $[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = <T1 className={t3}>{t4}{t5}{t6}</T1>;
    $[8] = T1;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = showPerWalletLimit ? perWalletLimit : undefined;
  let t9;
  if ($[13] !== isPreview || $[14] !== t8) {
    t9 = <MintCta isPreview={isPreview} perWalletLimit={t8} />;
    $[13] = isPreview;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== T0 || $[17] !== rest || $[18] !== t7 || $[19] !== t9) {
    t10 = <T0 className={t2} {...rest}>{t7}{t9}</T0>;
    $[16] = T0;
    $[17] = rest;
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}
function _temp3(state) {
  return [state.status, state.totalSupply, state.activeStageIndex, state.maxSupply, state.eligiblePrice, state.stages];
}